import { Injectable } from '@angular/core';
import { MTMCustomFile, TransferType } from './mtm-custom-file';
import { EMPTY, Observable } from 'rxjs';
import { TransferStatus } from './transfer-status';
import {
  listenerFileDownloadRemoved,
  listenerPreUploadCompleted,
  listenerUploadPendingScan,
  listenerUploadProgressChanged,
  listenerUploadStatusChanged,
} from './listeners';
import { supportedFileExtensions } from './file-upload';
import { UploadRequest, FileUploader } from './file.uploader';
import { SignedUrlUploader } from './signed-url-uploader';
import { DirectFileUploader } from './direct-file-uploader';
import { TransferMonitorService } from './transfer-monitor.service';
import { AuthService } from '../auth.service';
import { MTMFileDownload } from './mtm-file-download';
import { SignedUrlDownloader } from './signed-url-downloader';
import { v4 as uuid4 } from 'uuid';
import { FailedTranscodingUploader } from './failed-transcoding-uploader';
import { MalwareScanMonitorService } from './malware-scan-monitor.service';
import saveAs from '../../utils/download';
const downloadChunkSize: number = 50 * 1024 * 1024; //50Mb

export type NodeUploadItem = {
  files: MTMCustomFile[];
  progress: number;
  isUploading: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class FileTransferService {
  filesToUpload: MTMCustomFile[] = [];
  filesToDownload: MTMFileDownload[] = [];
  uploadRecord: Record<string, MTMCustomFile> = {};
  downloadRecord: Record<string, MTMFileDownload> = {};
  optionRecord: Record<string, NodeUploadItem> = {};
  topicRecord: Record<string, NodeUploadItem> = {};
  versionRecord: Record<string, NodeUploadItem> = {};

  constructor(
    private readonly resumableUploader: SignedUrlUploader,
    public readonly transferMonitor: TransferMonitorService,
    private readonly authService: AuthService,
    private readonly directUploader: DirectFileUploader,
    private readonly failedTranscodingUploader: FailedTranscodingUploader,
    private readonly scanMonitorService: MalwareScanMonitorService,
    private readonly signedUrlDownloader: SignedUrlDownloader
  ) {
    listenerPreUploadCompleted.subscribe((file: MTMCustomFile) => {
      if (!file?.dbFileObject?.id) {
        return;
      }
      this.uploadRecord[file.dbFileObject.id] = file;
    });

    listenerUploadPendingScan.subscribe((file: MTMCustomFile) => {
      this.removeFileFromRecord(file);
    });

    listenerUploadProgressChanged.subscribe((file: MTMCustomFile) => {
      this.calculateAggregateProgress(file);
    });
  }

  hasActiveTransfers() {
    const activeStatusList = [TransferStatus.SENDING, TransferStatus.WAITING, TransferStatus.DOWNLOADING];
    return (
      this.filesToUpload.some((file) => activeStatusList.includes(file.uploadStatusCode)) ||
      this.filesToDownload.some((file) => activeStatusList.includes(file.status))
    );
  }

  private calculateAggregateProgress(file: MTMCustomFile) {
    if (file.entity.id) {
      const optionId = file.entity.id;
      const { topicId } = file.entity;
      const version = `${optionId}-${file.entity.version}`;

      this.checkParentRecord(optionId, file, 'option');
      this.checkParentRecord(topicId, file, 'topic');
      this.checkParentRecord(version, file, 'version');
      this.calculateParentProgress(optionId, 'option');
      this.calculateParentProgress(topicId, 'topic');
      this.calculateParentProgress(version, 'version');
    }
  }

  private checkParentRecord(parentId: string, file: MTMCustomFile, level: 'topic' | 'option' | 'version') {
    if (!file) {
      return;
    }

    let record = this.optionRecord;
    if (level == 'topic') {
      record = this.topicRecord;
    } else if (level == 'version') {
      record = this.versionRecord;
    }

    if (!record[parentId]) {
      record[parentId] = {
        files: [file],
        progress: 0,
        isUploading: true,
      };
    } else if (!record[parentId].files.includes(file)) {
      record[parentId].files.push(file);
      record[parentId].isUploading = true;
    }
  }

  private removeFileFromRecord(file: MTMCustomFile) {
    if (!file) {
      return;
    }

    const optionId = file.entity.id;
    const { topicId } = file.entity;
    const version = `${optionId}-${file.entity.version}`;

    if (optionId && this.optionRecord[optionId]) {
      this.optionRecord[optionId].files = this.optionRecord[optionId].files.filter((f) => f != file);
      this.calculateParentProgress(optionId, 'option');
    }

    if (topicId && this.topicRecord[topicId]) {
      this.topicRecord[topicId].files = this.topicRecord[topicId].files.filter((f) => f != file);
      this.calculateParentProgress(topicId, 'topic');
    }

    if (version && this.versionRecord[version]) {
      this.versionRecord[version].files = this.versionRecord[version].files.filter((f) => f != file);
      this.calculateParentProgress(version, 'version');
    }

    delete this.uploadRecord[file.dbFileObject.id];
  }

  private calculateParentProgress(itemId: string, level: 'topic' | 'option' | 'version') {
    if (!itemId) {
      return;
    }

    let record = this.optionRecord;
    if (level == 'topic') {
      record = this.topicRecord;
    } else if (level == 'version') {
      record = this.versionRecord;
    }

    if (!record[itemId]) {
      return;
    }

    const item = record[itemId];
    let totalSize = 0,
      totalUploaded = 0;

    if (item.files.length) {
      item.files.forEach((file) => {
        totalUploaded += file.extraInfo?.totalUploaded;
        totalSize += file.fileSize;
      });
      item.progress = Math.floor((totalUploaded * 100) / totalSize);
      item.isUploading = true;
    } else {
      item.progress = 0;
      item.isUploading = false;
    }
  }

  ///#region upload files

  uploadFiles(request: UploadRequest, hideProgress = false) {
    const owner = this.authService.getAuthUserName();
    request.files.forEach((file) => {
      file.transferType = request.uploadType;
      file.owner = owner;
      this.sanitizeFileNames(file);
      file.setTimeToStartUploading();
      file.setUploadStatusCode(TransferStatus.WAITING);
    });

    if (!hideProgress) {
      this.filesToUpload.push(...request.files);
    }

    switch (request.uploadType) {
      case TransferType.SignedURL:
      case TransferType.Chunked:
        this.resumableUploader.uploadFiles(request);
        break;
      case TransferType.TranscodingFailed:
        this.failedTranscodingUploader.uploadFiles(request);
        break;
      case TransferType.DirectUpload:
        this.directUploader.uploadFiles(request);
        break;
    }
  }

  getUploaderByType(uploadType: string): FileUploader {
    switch (uploadType) {
      case TransferType.SignedURL:
      case TransferType.Chunked:
        return this.resumableUploader;
      case TransferType.DirectUpload:
        return this.directUploader;
      case TransferType.TranscodingFailed:
        return this.failedTranscodingUploader;
    }

    return null;
  }

  private sanitizeFileNames(file: MTMCustomFile) {
    const fileStringArray = file.fileName.split('.');
    const fileNameArr = fileStringArray.length > 1 ? file.fileName.split('.').slice(0, -1) : fileStringArray;
    let ext = fileStringArray.length > 1 ? file.fileName.split('.').pop() : '';
    if (ext) {
      ext = ext.toLowerCase();
    }
    const originalName = fileNameArr.join('.');
    let fileName = originalName.replace(/[^a-zA-Z0-9_\-]/g, '_') || 'mtm-file';
    const isSameName = originalName == fileName;
    if (!isSameName) {
      file.name = fileName + (ext ? ext : '');
    }
    const foundIndex = supportedFileExtensions.indexOf(ext) >= 0;
    if (!foundIndex) {
      const contentType = file.fileType || file.type;
      const typeExt = contentType.substr(contentType.indexOf('/') + 1);
      const foundExtIndex = supportedFileExtensions.indexOf(typeExt) >= 0;
      if (foundExtIndex) {
        if (typeExt === 'vnd.openxmlformats-officedocument.presentationml.presentation') {
          ext = 'pptx';
        } else if (typeExt === 'vnd.ms-powerpoint') {
          ext = 'ppt';
        } else if (typeExt === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          ext = 'xlsx';
        } else if (typeExt === 'vnd.ms-excel') {
          ext = 'xls';
        } else if (typeExt === 'vnd.openxmlformats-officedocument.wordprocessingml.document') {
          ext = 'docx';
        } else if (typeExt === 'msword') {
          ext = 'docx';
        } else {
          ext = typeExt;
        }
        fileName = fileStringArray.join('.');
      } else {
        fileName = fileNameArr.join('.');
      }
    }
    file.fileName = fileName + '_' + (file.createTime || (file as any).timeCreated) + (ext ? '.' + ext : '');
  }

  pauseAllTransfers() {
    if (this.filesToUpload.length > 0) {
      this.filesToUpload.forEach((file) => {
        this.cancelUpload(file);
      });
    }
  }

  resumeUpload(file: MTMCustomFile) {
    if (!file.isResumableFromError()) {
      return;
    }
    const wasInactive = file.uploadStatusCode == TransferStatus.INACTIVE;
    this.resumableUploader.resumeUpload(file);
    if (wasInactive) {
      this.transferMonitor.sendFileToMonitor(file.preUploadUrl, file.entity, file);
    }
  }

  cancelUpload(file: MTMCustomFile): Observable<any> {
    let isEventTriggered = false;
    if (file.uploadStatusCode != TransferStatus.COMPLETED && file.uploadStatusCode != TransferStatus.SCANNING) {
      file.uploadStatusCode = TransferStatus.CANCELED;
      file.progress$.next({
        step: 'cancel',
        startIndex: file.startIndex,
        status: TransferStatus.CANCELED,
      });
      isEventTriggered = true;
    }
    this.filesToUpload.splice(this.filesToUpload.indexOf(file), 1);
    if (!isEventTriggered) {
      listenerUploadStatusChanged.next(file);
    }
    delete this.uploadRecord[file.dbFileObject.id];
    const uploader = this.getUploaderByType(file.transferType);
    if (!uploader) {
      return EMPTY;
    }

    this.transferMonitor.removeFileFromUploadMonitor(file.preUploadUrl, file);
    this.scanMonitorService.stopMonitoringFile(file.dbFileObject?.id);
    this.removeFileFromRecord(file);
    return uploader.cancelUpload(file);
  }

  //#endregion

  //#region download files

  downloadFile(file: MTMFileDownload) {
    if (!file.id) {
      file.id = uuid4();
    }
    file.startDate = +new Date();
    file.owner = this.authService.getAuthUserName();
    this.signedUrlDownloader.download(file);
    if (!file.isSilent) {
      this.filesToDownload.push(file);
      this.downloadRecord[file.id] = file;
    }
    this.handleDownloadCompleted(file);
  }

  cancelDownload(file: MTMFileDownload) {
    if (file.status == TransferStatus.COMPLETED) {
      this.filesToDownload.splice(this.filesToDownload.indexOf(file), 1);
      listenerFileDownloadRemoved.next(file);
      return;
    }

    this.signedUrlDownloader.cancelDownload(file);
    this.filesToDownload.splice(this.filesToDownload.indexOf(file), 1);
    delete this.downloadRecord[file.id];
    listenerFileDownloadRemoved.next(file);
  }

  handleDownloadCompleted(file: MTMFileDownload): void {
    file.done$.subscribe((file: MTMFileDownload) => {
      const blob: Blob = new Blob([file.buffer.buffer as ArrayBuffer], { type: file.contentType });
      saveAs(blob, file.displayName || file.name, {});
    });
  }

  //#endregion
}
